class DynamicCampaignContent {
  constructor() {
    this.initialized = false;
    this.maxRetries = 10;
    this.retryCount = 0;
    this.retryDelay = 100; // ms
    
    this.waitForCampaignData();
  }

  waitForCampaignData() {
    // Check if campaign data is already available
    if (window.campaignData && window.campaignData.campaigns) {
      this.initializeCampaign();
      return;
    }

    // Listen for the custom event
    window.addEventListener('campaignDataReady', (event) => {
      console.log('Campaign data ready event received:', event.detail);
      this.initializeCampaign();
    });

    // Also try polling as a fallback
    this.pollForCampaignData();
  }

  pollForCampaignData() {
    const checkData = () => {
      if (window.campaignData && window.campaignData.campaigns) {
        console.log('Campaign data found via polling');
        this.initializeCampaign();
      } else if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        console.log(`Waiting for campaign data... attempt ${this.retryCount}/${this.maxRetries}`);
        setTimeout(checkData, this.retryDelay);
      } else {
        console.error('Campaign data not found after maximum retries');
      }
    };

    checkData();
  }

  initializeCampaign() {
    if (this.initialized) return; // Prevent multiple initializations
    this.initialized = true;

    this.campaignData = window.campaignData || {};
    this.currentCampaign = this.campaignData.currentCampaign || "default";
    this.campaigns = this.campaignData.campaigns || {};

    console.log('Initializing campaign:', this.currentCampaign);
    console.log('Available campaigns:', Object.keys(this.campaigns));

    if (
      this.currentCampaign !== "default" &&
      this.campaigns[this.currentCampaign]
    ) {
      this.init();
    } else {
      console.log('Using default campaign or campaign not found');
    }
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        this.applyDynamicContent()
      );
    } else {
      // Add a small delay to ensure all elements are rendered
      setTimeout(() => this.applyDynamicContent(), 100);
    }
  }

  applyDynamicContent() {
    const campaign = this.campaigns[this.currentCampaign];
    if (!campaign) {
      console.error('Campaign data not found for:', this.currentCampaign);
      return;
    }

    console.log('Applying campaign:', this.currentCampaign);

    try {
      // Update product title
      this.updateTitle(campaign.title);

      // Update product description
      this.updateDescription(campaign.description);

      // Update benefits
      this.updateBenefits(campaign.benefits);

      // Update FAQs
      this.updateFAQs(campaign.faqs);

      // Update images
      console.log('About to update images with:', campaign.images);
      this.updateImages(campaign.images);

      // Update Image with Text section
      this.updateImageWithText(campaign.imageWithText);

      // Update testimonial quote
      this.updateTestimonialQuote(campaign.testimonialQuote);

      // Trigger any necessary theme updates
      this.triggerThemeUpdates();
      
      console.log('Campaign content applied successfully');
    } catch (error) {
      console.error('Error applying campaign content:', error);
    }
  }

  updateTitle(newTitle) {
    if (!newTitle) return;

    // Find all title elements
    const titleElements = document.querySelectorAll(
      '.product-details-product-title, h1[class*="product-title"], .product-details-wrapper h1'
    );
    
    console.log(`Found ${titleElements.length} title elements`);
    
    titleElements.forEach((el) => {
      el.textContent = newTitle;
    });

    // Update page title
    document.title = document.title.replace(/^[^|]+/, newTitle + " ");
  }

  updateDescription(newDescription) {
    if (!newDescription) return;

    // Find product description block
    const descriptionBlocks = document.querySelectorAll(
      ".product-description:not(.product-description--collapsible) .text-link-animated"
    );
    
    console.log(`Found ${descriptionBlocks.length} description blocks`);
    
    descriptionBlocks.forEach((el) => {
      el.innerHTML = newDescription;
    });

    // Update collapsible description if exists
    const collapsibleDesc = document.querySelector(
      ".product-description--collapsible .collapsible-content .content"
    );
    if (collapsibleDesc) {
      collapsibleDesc.innerHTML = newDescription;
    }
  }

  updateBenefits(benefits) {
    if (!benefits || !benefits.length) return;

    // Find all benefit blocks - using the correct selector
    const benefitBlocks = document.querySelectorAll(
      '[data-update-id*="item-text_with_icon"] .text-with-icon'
    );

    console.log(`Found ${benefitBlocks.length} benefit blocks, have ${benefits.length} benefits to apply`);

    benefits.forEach((benefit, index) => {
      if (benefitBlocks[index]) {
        const block = benefitBlocks[index];

        // Update text only - using the correct class
        const textElement = block.querySelector(".text-with-icon--text");
        if (textElement) {
          textElement.textContent = benefit.text;
          console.log(`Updated benefit ${index}: ${benefit.text.substring(0, 50)}...`);
        }
      }
    });
  }

  updateFAQs(faqs) {
    if (!faqs || !faqs.length) return;

    // Find FAQ blocks - using the correct selector
    const faqBlocks = document.querySelectorAll(
      '.collapsible-tab-container[data-update-id*="item-collapsible_tab"]'
    );

    console.log(`Found ${faqBlocks.length} FAQ blocks, have ${faqs.length} FAQs to apply`);

    faqs.forEach((faq, index) => {
      if (faqBlocks[index]) {
        const block = faqBlocks[index];

        // Update question in the JS version
        const questionElement = block.querySelector(
          ".collapsible-tab.js .summary .text-with-icon--text"
        );
        if (questionElement) {
          questionElement.textContent = faq.question;
        }

        // Also update question in the no-js fallback version
        const noJsQuestion = block.querySelector(
          ".no-js .summary .text-with-icon--text"
        );
        if (noJsQuestion) {
          noJsQuestion.textContent = faq.question;
        }

        // Update answer in the JS version
        const answerElement = block.querySelector(
          ".collapsible-tab.js .collapsible-content .content"
        );
        if (answerElement) {
          // Clear existing content and add new
          answerElement.innerHTML = "";
          const answerDiv = document.createElement("div");
          answerDiv.className = "text-link-animated richtext-block";
          answerDiv.innerHTML = `<p>${faq.answer}</p>`;
          answerElement.appendChild(answerDiv);
        }

        // Also update answer in the no-js fallback version
        const noJsAnswer = block.querySelector(
          ".no-js .rte"
        );
        if (noJsAnswer) {
          noJsAnswer.innerHTML = `<p>${faq.answer}</p>`;
        }
        
        console.log(`Updated FAQ ${index}: ${faq.question}`);
      }
    });
  }

  updateImages(images) {
    console.log('updateImages called with:', images);
    if (!images || !images.gallery || !images.gallery.length) {
      console.log('No images data, exiting');
      return;
    }

    // Add delay to ensure swiper is initialized
    setTimeout(() => {
      console.log('Attempting image update after delay...');
      
      // Find all swiper slides with data-media-id
      const allMediaSlides = document.querySelectorAll('.swiper-slide[data-media-id]');
      console.log('Total media slides found:', allMediaSlides.length);
      
      // Group slides by their media ID to handle duplicates
      const slidesByMediaId = {};
      allMediaSlides.forEach(slide => {
        const mediaId = slide.dataset.mediaId;
        if (!slidesByMediaId[mediaId]) {
          slidesByMediaId[mediaId] = [];
        }
        slidesByMediaId[mediaId].push(slide);
      });
      
      // Get unique media IDs in order
      const uniqueMediaIds = Object.keys(slidesByMediaId);
      console.log('Unique media IDs:', uniqueMediaIds.length);
      
      // Update images by index
      images.gallery.forEach((newSrc, index) => {
        if (index < uniqueMediaIds.length && newSrc) {
          const mediaId = uniqueMediaIds[index];
          const slides = slidesByMediaId[mediaId];
          
          console.log(`Updating media ID ${mediaId} (${slides.length} instances) to:`, newSrc);
          
          slides.forEach(slide => {
            const img = slide.querySelector('img');
            if (img) {
              // Ensure proper protocol
              const fullSrc = newSrc.startsWith('//') ? 'https:' + newSrc : newSrc;
              img.src = fullSrc;
              img.srcset = ''; // Clear srcset
              
              // Also update lazy load attributes if present
              if (img.dataset.src) {
                img.dataset.src = fullSrc;
              }
              if (img.dataset.srcset) {
                img.dataset.srcset = '';
              }
              
              // Update loading attribute to ensure image loads
              img.loading = 'eager';
            }
          });
        }
      });
      
      // Force browser to reload images
      window.dispatchEvent(new Event('resize'));
      
    }, 500); // Wait 500ms for swiper initialization
  }

  updateImageWithText(imageWithTextData) {
    if (!imageWithTextData) return;

    console.log('Updating image with text section:', imageWithTextData);

    // Update the title/heading
    if (imageWithTextData.title) {
      const titleElement = document.querySelector('.image-with-text__container .homepage-sections--title');
      if (titleElement) {
        titleElement.textContent = imageWithTextData.title;
        console.log('Updated image with text title');
      }
    }

    // Update the content/text
    if (imageWithTextData.content) {
      const contentElement = document.querySelector('.image-with-text__container .featured-row__subtext');
      if (contentElement) {
        contentElement.innerHTML = imageWithTextData.content;
        console.log('Updated image with text content');
      }
    }

    // Update the image
    if (imageWithTextData.image) {
      // Find the image in the rellax container
      const imageElement = document.querySelector('.image-with-text__container .img img');
      if (imageElement) {
        imageElement.src = imageWithTextData.image;
        imageElement.srcset = ''; // Clear srcset to force new image
        imageElement.loading = 'eager'; // Force immediate loading
        console.log('Updated image with text image');
        
        // Also update the noscript version for accessibility
        const noscriptImg = document.querySelector('.image-with-text__container .img noscript img');
        if (noscriptImg) {
          const newNoscript = `<img class="theme-img" src="${imageWithTextData.image}" alt="" style="object-fit:cover;object-position:50.0% 50.0%!important;" loading="lazy" />`;
          noscriptImg.parentElement.innerHTML = newNoscript;
        }
      }
    }
  }

  updateTestimonialQuote(quote) {
    if (!quote) return;

    // Find all h2.homepage-sections--title elements
    const allH2s = document.querySelectorAll('h2.homepage-sections--title');
    
    console.log(`Found ${allH2s.length} h2 elements, looking for testimonial`);
    
    // Find the first one that starts and ends with quotes
    for (let i = 0; i < allH2s.length; i++) {
      const h2 = allH2s[i];
      const text = h2.textContent.trim();
      
      // Check if this is a testimonial (starts and ends with quotes)
      if (text.charAt(0) === '"' && text.charAt(text.length - 1) === '"') {
        h2.textContent = `"${quote}"`;
        console.log('Updated testimonial quote at index', i, 'to:', quote);
        break; // Only update the first matching testimonial
      }
    }
  }

  triggerThemeUpdates() {
    // Trigger any theme-specific update events
    window.dispatchEvent(new Event('resize'));

    // If using a slider for images, reinitialize it
    if (window.theme && window.theme.ProductMedia) {
      window.theme.ProductMedia.init();
    }

    // Dispatch custom event to signal updates are complete
    window.dispatchEvent(new CustomEvent('campaignContentApplied', { 
      detail: { campaign: this.currentCampaign } 
    }));
  }
}

// Initialize when script loads
document.addEventListener('DOMContentLoaded', () => {
  new DynamicCampaignContent();
});

// Also try immediate initialization in case DOM is already ready
if (document.readyState !== 'loading') {
  new DynamicCampaignContent();
}